import { AnimatePresence,motion } from 'framer-motion';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getDataCollectionQuestions } from './dataCollectionQuestions';
import { DataCollectionScreen } from './DataCollectionScreen';
import { useIntakeFormData } from './IntakeFormDataContext';
import {
  computeQualificationOutcome,
  QualificationScreen,
} from './QualificationScreen';
import { getQualificationQuestions } from './questions';
import ReviewScreen from './ReviewScreen';
import { ScreeningV2Sidebar } from './ScreeningV2Sidebar';
import {
  createInitialScreeningV2Data,
  DataCollectionAnswer,
  QualificationAnswer,
  QualificationOutcome,
  ScreeningV2Data,
} from './types';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ScreeningV2LeftPanelProps {
  /** Intake ID for companion case operations */
  intakeId?: string;
  caseType: string;
  /** Initial V2 data if resuming a session */
  initialV2Data?: ScreeningV2Data | null;
  /** Loss date from header (two-way sync) */
  incidentDate: string;
  /** State from header (two-way sync) */
  incidentState: string;
  /** Callback when incident date changes */
  onIncidentDateChange: (date: string) => void;
  /** Callback when incident state changes */
  onIncidentStateChange: (state: string) => void;
  /** Injured body parts from incident_info */
  injuredBodyParts: string[];
  /** Pain level from incident_info */
  painLevel: number | null;
  /** Callback when injured body parts change */
  onInjuredBodyPartsChange: (parts: string[]) => void;
  /** Callback when pain level changes */
  onPainLevelChange: (level: number | null) => void;
  /** Callback when V2 data changes (for auto-save) */
  onV2DataChange: (data: ScreeningV2Data) => void;
  /** Callback when retainer should be sent via CMS */
  onSendRetainer: () => void;
  /** Callback when retainer is marked as sent manually (updates loe_status) */
  onMarkRetainerSentManually: () => void;
  /** Callback when intake should be rejected */
  onReject: (reason: string) => void;
  /** Whether retainer is currently being sent */
  isRetainerSending: boolean;
  /** Current user ID */
  userId: string | null;
  /** Current user name */
  userName: string;
  /** Field ID to highlight (for deep-link navigation from End Intake) */
  highlightedFieldId?: string | null;
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export const ScreeningV2LeftPanel: React.FC<ScreeningV2LeftPanelProps> = ({
  intakeId,
  caseType,
  initialV2Data,
  incidentDate,
  incidentState,
  onIncidentDateChange,
  onIncidentStateChange,
  injuredBodyParts,
  painLevel,
  onInjuredBodyPartsChange,
  onPainLevelChange,
  onV2DataChange,
  onSendRetainer,
  onMarkRetainerSentManually,
  onReject,
  isRetainerSending,
  userId,
  userName,
  highlightedFieldId,
}) => {
  // Shared form data context (single source of truth for all form fields)
  const formDataCtx = useIntakeFormData();

  // ---------------------------------------------------------------------------
  // V2 State Management
  // ---------------------------------------------------------------------------

  const [v2Data, setV2Data] = useState<ScreeningV2Data>(() => {
    // Default to collect screen (2) only for Mold (data collection only, no P0 questions)
    const defaultScreen = caseType?.toLowerCase() === 'mold' ? 2 : 1;

    if (initialV2Data?.version === 'v2') {
      // Ensure currentScreen is valid (1, 2, 3, or 4)
      const safeCurrentScreen = [1, 2, 3, 4].includes(initialV2Data.currentScreen)
        ? initialV2Data.currentScreen
        : defaultScreen;
      return {
        ...initialV2Data,
        currentScreen: safeCurrentScreen as 1 | 2 | 3 | 4,
      };
    }
    const initial = createInitialScreeningV2Data(userId, userName);
    initial.currentScreen = defaultScreen as 1 | 2 | 3;
    return initial;
  });

  // Track previous screen for animation direction - compute synchronously during render
  const prevScreenRef = useRef(v2Data.currentScreen);

  // Compute direction synchronously (before render) so animations use correct value
  let direction = 1;
  if (v2Data.currentScreen !== prevScreenRef.current) {
    direction = v2Data.currentScreen > prevScreenRef.current ? 1 : -1;
  }

  // Update ref after computing direction (will be used on next render)
  useEffect(() => {
    prevScreenRef.current = v2Data.currentScreen;
  }, [v2Data.currentScreen]);

  // Sync initial V2 data to parent on mount so validation has data immediately
  useEffect(() => {
    onV2DataChange(v2Data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Sync currentScreen from parent when it changes externally (e.g., from End Intake deep-link)
  useEffect(() => {
    if (
      initialV2Data?.currentScreen &&
      [1, 2, 3, 4].includes(initialV2Data.currentScreen)
    ) {
      setV2Data((prev) => {
        // Only update if different to avoid unnecessary re-renders
        if (prev.currentScreen !== initialV2Data.currentScreen) {
          return {
            ...prev,
            currentScreen: initialV2Data.currentScreen as 1 | 2 | 3 | 4,
          };
        }
        return prev;
      });
    }
  }, [initialV2Data?.currentScreen]);

  // Sync retainerSentAt from parent when it changes externally (e.g., from footer's Mark as Sent Manually)
  useEffect(() => {
    if (initialV2Data?.retainerSentAt) {
      setV2Data((prev) => {
        // Only update if different to avoid unnecessary re-renders
        if (prev.retainerSentAt !== initialV2Data.retainerSentAt) {
          return {
            ...prev,
            retainerSentAt: initialV2Data.retainerSentAt,
            qualificationOutcome: initialV2Data.qualificationOutcome ?? prev.qualificationOutcome,
            qualificationCompletedAt: initialV2Data.qualificationCompletedAt ?? prev.qualificationCompletedAt,
          };
        }
        return prev;
      });
    }
  }, [initialV2Data?.retainerSentAt, initialV2Data?.qualificationOutcome, initialV2Data?.qualificationCompletedAt]);

  // Sync dataCollectionAnswers from parent when it changes externally
  // This enables two-way sync: edits in intake form → screening sheet
  // (Same pattern as header fields like phone/email)
  useEffect(() => {
    if (initialV2Data?.dataCollectionAnswers) {
      setV2Data((prev) => ({
        ...prev,
        dataCollectionAnswers: initialV2Data.dataCollectionAnswers,
      }));
    }
  }, [initialV2Data?.dataCollectionAnswers]);

  // Get questions for this case type
  const questions = useMemo(
    () => getQualificationQuestions(caseType),
    [caseType]
  );

  // Get data collection questions for this case type
  const dataCollectionQuestions = useMemo(
    () => getDataCollectionQuestions(caseType),
    [caseType]
  );

  // Check if this case type has data collection questions
  const hasDataCollectionQuestions = dataCollectionQuestions.length > 0;

  // Compute qualification outcome
  const qualificationOutcome = useMemo(
    () =>
      computeQualificationOutcome(
        questions,
        v2Data.qualificationAnswers,
        v2Data.qualificationContextAsked
      ),
    [questions, v2Data.qualificationAnswers, v2Data.qualificationContextAsked]
  );

  // Check if escalation is recommended (any unknown answers)
  const isEscalationRecommended = useMemo(() => {
    const answers = v2Data.qualificationAnswers ?? {};
    return questions.some((q) => answers[q.id] === 'unknown');
  }, [questions, v2Data.qualificationAnswers]);

  // ---------------------------------------------------------------------------
  // Update helpers that also notify parent
  // ---------------------------------------------------------------------------

  const updateV2Data = useCallback(
    (updater: (prev: ScreeningV2Data) => ScreeningV2Data) => {
      setV2Data((prev) => {
        const next = updater(prev);
        onV2DataChange(next);
        return next;
      });
    },
    [onV2DataChange]
  );

  // ---------------------------------------------------------------------------
  // Screen 1: Qualification Handlers
  // ---------------------------------------------------------------------------

  const handleAnswerChange = useCallback(
    (questionId: string, answer: QualificationAnswer) => {
      updateV2Data((prev) => ({
        ...prev,
        qualificationAnswers: {
          ...prev.qualificationAnswers,
          [questionId]: answer,
        },
      }));
    },
    [updateV2Data]
  );

  const handleContextAsked = useCallback(
    (questionId: string) => {
      updateV2Data((prev) => ({
        ...prev,
        qualificationContextAsked: {
          ...prev.qualificationContextAsked,
          [questionId]: !prev.qualificationContextAsked[questionId],
        },
      }));
    },
    [updateV2Data]
  );

  const handleResetQuestion = useCallback(
    (questionId: string) => {
      updateV2Data((prev) => {
        const newAnswers = { ...prev.qualificationAnswers };
        delete newAnswers[questionId];
        const newContextAsked = { ...prev.qualificationContextAsked };
        delete newContextAsked[questionId];
        // Note: We preserve the note when resetting - user can clear it manually if needed
        return {
          ...prev,
          qualificationAnswers: newAnswers,
          qualificationContextAsked: newContextAsked,
        };
      });
    },
    [updateV2Data]
  );

  const handleNoteChange = useCallback(
    (questionId: string, note: string) => {
      updateV2Data((prev) => ({
        ...prev,
        qualificationNotes: {
          ...prev.qualificationNotes,
          [questionId]: note,
        },
      }));

      // Sync note to form_data if question has noteFormDataPath
      if (formDataCtx) {
        const question = questions.find((q) => q.id === questionId);
        if (question?.noteFormDataPath) {
          formDataCtx.setField(question.noteFormDataPath, note);
        }
      }
    },
    [updateV2Data, formDataCtx, questions]
  );

  const handleSendRetainer = useCallback(() => {
    // Note: retainerSentAt is set by ScreeningSheet's handleLoeSent after LOE is actually sent
    // Here we just trigger the LOE dialog
    onSendRetainer();
  }, [onSendRetainer]);

  const handleMarkRetainerSentManually = useCallback(() => {
    // Set retainerSentAt locally
    updateV2Data((prev) => ({
      ...prev,
      retainerSentAt: new Date().toISOString(),
      qualificationOutcome: 'qualified' as QualificationOutcome,
      qualificationCompletedAt: new Date().toISOString(),
    }));
    // Call parent to update loe_status to "Sent (Manual)"
    onMarkRetainerSentManually();
  }, [updateV2Data, onMarkRetainerSentManually]);

  const handleReject = useCallback((reason: string) => {
    updateV2Data((prev) => ({
      ...prev,
      qualificationOutcome: 'not_qualified' as QualificationOutcome,
      qualificationCompletedAt: new Date().toISOString(),
      rejectedAt: new Date().toISOString(),
    }));
    onReject(reason);
  }, [updateV2Data, onReject]);

  // ---------------------------------------------------------------------------
  // Screen 2: Data Collection Handlers
  // ---------------------------------------------------------------------------

  // Boolean form_data paths that need 'yes'/'no' → true/false conversion
  const BOOLEAN_PATHS = useMemo(() => new Set([
    'incident_info.police_report',
    'incident_info.ems',
    'incident_info.photos',
    'incident_info.currently_treating',
    'medical_wage_history.has_lost_wages',
  ]), []);

  // Build a flat lookup map of all questions + sub-questions by ID
  const questionById = useMemo(() => {
    const map = new Map<string, { id: string; formDataPath?: string }>();
    dataCollectionQuestions.forEach((q) => {
      map.set(q.id, q);
      q.subQuestions?.forEach((sub) => map.set(sub.id, sub));
    });
    return map;
  }, [dataCollectionQuestions]);

  const handleDataAnswerChange = useCallback(
    (questionId: string, value: string | undefined) => {
      updateV2Data((prev) => {
        const existingAnswer = prev.dataCollectionAnswers[questionId];
        return {
          ...prev,
          dataCollectionAnswers: {
            ...prev.dataCollectionAnswers,
            [questionId]: {
              value,
              asked: existingAnswer?.asked ?? false,
            } as DataCollectionAnswer,
          },
        };
      });

      // Direct-write to context if question has a formDataPath
      // Searches both top-level questions AND sub-questions via flat lookup map
      // Skip 'none' (UI-only marker for "no items") and object_array questions
      // (those are handled by handleDataArrayChange)
      if (formDataCtx && value !== undefined && value !== 'none') {
        const question = questionById.get(questionId);
        if (question?.formDataPath) {
          // Skip array paths — writing a string to an array field corrupts data
          if (question.formDataPath.includes('treatments')) return;
          const writeValue = BOOLEAN_PATHS.has(question.formDataPath)
            ? (value === 'yes' || value === 'true')
            : value;
          formDataCtx.setField(question.formDataPath, writeValue);
        }
      }
    },
    [updateV2Data, formDataCtx, questionById, BOOLEAN_PATHS]
  );

  const handleDataAskedChange = useCallback(
    (questionId: string, asked: boolean) => {
      updateV2Data((prev) => {
        const existingAnswer = prev.dataCollectionAnswers[questionId];
        return {
          ...prev,
          dataCollectionAnswers: {
            ...prev.dataCollectionAnswers,
            [questionId]: {
              value: existingAnswer?.value,
              asked,
            } as DataCollectionAnswer,
          },
        };
      });
    },
    [updateV2Data]
  );

  const handleDataArrayChange = useCallback(
    (questionId: string, arrayValue: Record<string, string>[]) => {
      updateV2Data((prev) => {
        const existingAnswer = prev.dataCollectionAnswers[questionId];
        return {
          ...prev,
          dataCollectionAnswers: {
            ...prev.dataCollectionAnswers,
            [questionId]: {
              value: existingAnswer?.value,
              asked: existingAnswer?.asked ?? false,
              arrayValue,
            } as DataCollectionAnswer,
          },
        };
      });

      // Direct-write treatments to context
      if (formDataCtx) {
        const question = dataCollectionQuestions.find((q) => q.id === questionId);
        if (question?.formDataPath) {
          // Convert V2 array format to backend Treatment format
          const treatments = arrayValue.map((item) => ({
            provider: item.provider_name || '',
            date_of_service: item.start_date || '',
            treatment_received: item.details || '',
            still_treating: !item.end_date,
          }));
          formDataCtx.setField(question.formDataPath, treatments);
        }
      }
    },
    [updateV2Data, formDataCtx, dataCollectionQuestions]
  );

  // const handleDataCollectionFinish = useCallback(() => {
  //   updateV2Data((prev) => ({
  //     ...prev,
  //     collectionCompletedAt: new Date().toISOString(),
  //     currentScreen: 3,
  //   }));
  // }, [updateV2Data]);

  // ---------------------------------------------------------------------------
  // Navigation
  // ---------------------------------------------------------------------------

  const handleNavigate = useCallback(
    (screen: 1 | 2 | 3 | 4) => {
      updateV2Data((prev) => ({
        ...prev,
        currentScreen: screen,
      }));
    },
    [updateV2Data]
  );

  // Determine screen statuses for sidebar
  const screen1Status = useMemo(() => {
    if (v2Data.retainerSentAt) return 'complete';
    if (qualificationOutcome === 'not_qualified') return 'blocked';
    return 'pending';
  }, [v2Data.retainerSentAt, qualificationOutcome]);

  const hasNoQualificationQuestions = useMemo(
    () => caseType?.toLowerCase() === 'mold',
    [caseType],
  );

  const screen2Status = useMemo(() => {
    // No data collection questions for this case type
    if (!hasDataCollectionQuestions) return 'locked';
    if (v2Data.collectionCompletedAt) return 'complete';
    // Always unlocked for case types with no qualification step (e.g. Mold)
    if (hasNoQualificationQuestions) return 'pending';
    // Unlock when qualified, retainer was sent, OR escalation is recommended (unknown answers)
    // Escalated cases still need P1 data collected for attorney review
    if (qualificationOutcome !== 'qualified' && !v2Data.retainerSentAt && !isEscalationRecommended) return 'locked';
    return 'pending';
  }, [hasDataCollectionQuestions, qualificationOutcome, v2Data.retainerSentAt, v2Data.collectionCompletedAt, isEscalationRecommended, hasNoQualificationQuestions]);

  // Screen 3: Review — always accessible
  const screen3Status: 'locked' | 'pending' | 'complete' = 'pending';

  // Screen 4: AI Extract — locked until future implementation
  const screen4Status: 'locked' | 'pending' | 'complete' = v2Data.reviewCompletedAt ? 'complete' : 'locked';

  // Determine if we should show the "Continue to Collect" prompt
  // Only show when:
  // 1. Retainer has been sent (either through app or manually marked)
  // 2. Has data collection questions for this case type
  // 3. End Intake hasn't been clicked yet (intake is still in progress)
  // 4. Still on screen 1 (qualification screen)
  const showCollectPrompt = useMemo(() => {
    return (
      !!v2Data.retainerSentAt &&
      hasDataCollectionQuestions &&
      !v2Data.endIntakeClickedAt &&
      !v2Data.firstContactOutcome &&
      v2Data.currentScreen === 1
    );
  }, [v2Data.retainerSentAt, hasDataCollectionQuestions, v2Data.endIntakeClickedAt, v2Data.firstContactOutcome, v2Data.currentScreen]);

  // Handler to navigate to collect screen
  const handleNavigateToCollect = useCallback(() => {
    handleNavigate(2);
  }, [handleNavigate]);

  // Data collection answers are now written directly to context when changed
  // (via handleDataAnswerChange and handleDataArrayChange), so no screen-3
  // navigation sync is needed.

  // Save all unsaved changes — context handles the mutation directly
  const handleSaveAll = useCallback(async () => {
    if (!formDataCtx?.isDirty) return;
    await formDataCtx.save();
  }, [formDataCtx]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="h-full relative overflow-hidden">
      {/* Floating Navigation Island */}
      <ScreeningV2Sidebar
        currentScreen={v2Data.currentScreen as 1 | 2 | 3 | 4}
        screen1Status={screen1Status}
        screen2Status={screen2Status}
        screen3Status={screen3Status}
        screen4Status={screen4Status}
        onNavigate={handleNavigate}
        showCollectStep={hasDataCollectionQuestions}
        hasUnsavedChanges={formDataCtx?.isDirty}
        onSaveNow={handleSaveAll}
      />

      {/* Screen Content with parallel slide animations */}
      <div className="h-full overflow-hidden relative">
        <AnimatePresence initial={false} custom={direction}>
          {/* Screen 1: Qualification */}
          {(v2Data.currentScreen === 1 || ![1, 2, 3, 4].includes(v2Data.currentScreen)) && (
            <motion.div
              key="screen-1"
              custom={direction}
              initial="enter"
              animate="center"
              exit="exit"
              variants={{
                enter: (dir: number) => ({ y: dir > 0 ? '100%' : '-100%' }),
                center: { y: 0 },
                exit: (dir: number) => ({ y: dir > 0 ? '-100%' : '100%' }),
              }}
              transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
              className="absolute inset-0"
            >
              <QualificationScreen
                questions={questions}
                answers={v2Data.qualificationAnswers}
                contextAsked={v2Data.qualificationContextAsked}
                notes={v2Data.qualificationNotes}
                outcome={qualificationOutcome}
                incidentDate={incidentDate}
                incidentState={incidentState}
                retainerSent={!!v2Data.retainerSentAt}
                onAnswerChange={handleAnswerChange}
                onContextAsked={handleContextAsked}
                onNoteChange={handleNoteChange}
                onResetQuestion={handleResetQuestion}
                onIncidentDateChange={onIncidentDateChange}
                onIncidentStateChange={onIncidentStateChange}
                injuredBodyParts={injuredBodyParts}
                painLevel={painLevel}
                onInjuredBodyPartsChange={onInjuredBodyPartsChange}
                onPainLevelChange={onPainLevelChange}
                onSendRetainer={handleSendRetainer}
                onMarkRetainerSentManually={handleMarkRetainerSentManually}
                onReject={handleReject}
                isRetainerSending={isRetainerSending}
                highlightedFieldId={highlightedFieldId}
                showCollectPrompt={showCollectPrompt}
                onNavigateToCollect={handleNavigateToCollect}
              />
            </motion.div>
          )}

          {/* Screen 2: Data Collection */}
          {v2Data.currentScreen === 2 && (
            <motion.div
              key="screen-2"
              custom={direction}
              initial="enter"
              animate="center"
              exit="exit"
              variants={{
                enter: (dir: number) => ({ y: dir > 0 ? '100%' : '-100%' }),
                center: { y: 0 },
                exit: (dir: number) => ({ y: dir > 0 ? '-100%' : '100%' }),
              }}
              transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
              className="absolute inset-0"
            >
              <DataCollectionScreen
                questions={dataCollectionQuestions}
                answers={v2Data.dataCollectionAnswers}
                onAnswerChange={handleDataAnswerChange}
                onAskedChange={handleDataAskedChange}
                onArrayChange={handleDataArrayChange}
                highlightedFieldId={highlightedFieldId}
              />
            </motion.div>
          )}

          {/* Screen 3: Review (Data Entry Surface) */}
          {v2Data.currentScreen === 3 && (
            <motion.div
              key="screen-3"
              custom={direction}
              initial="enter"
              animate="center"
              exit="exit"
              variants={{
                enter: (dir: number) => ({ y: dir > 0 ? '100%' : '-100%' }),
                center: { y: 0 },
                exit: (dir: number) => ({ y: dir > 0 ? '-100%' : '100%' }),
              }}
              transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
              className="absolute inset-0"
            >
              {formDataCtx ? (
                <ReviewScreen
                  formData={formDataCtx.formData}
                  onFieldChange={formDataCtx.setField}
                  caseDescription={formDataCtx.formData?.incident_info?.description ?? ''}
                  onCaseDescriptionChange={(val) => formDataCtx.setField('incident_info.description', val)}
                  intakeId={intakeId}
                  summary={formDataCtx.intake?.summary}
                />
              ) : (
                <div className="h-full flex items-center justify-center text-zinc-400 bg-zinc-50">
                  <div className="text-center">
                    <p className="text-lg font-bold">Review Screen</p>
                    <p className="text-sm font-medium mt-2">Loading form data...</p>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* Screen 4: AI Extract */}
          {v2Data.currentScreen === 4 && (
            <motion.div
              key="screen-4"
              custom={direction}
              initial="enter"
              animate="center"
              exit="exit"
              variants={{
                enter: (dir: number) => ({ y: dir > 0 ? '100%' : '-100%' }),
                center: { y: 0 },
                exit: (dir: number) => ({ y: dir > 0 ? '-100%' : '100%' }),
              }}
              transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
              className="absolute inset-0"
            >
              <div className="h-full flex items-center justify-center text-zinc-400 bg-zinc-50">
                <div className="text-center">
                  <p className="text-lg font-bold">AI Extract Screen</p>
                  <p className="text-sm mt-2">Coming soon...</p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ScreeningV2LeftPanel;
